import { ApiError, getAuthToken } from '../../api/client';
import { t } from '../../i18n';

/**
 * Адреса картинок и загрузка файлов.
 *
 * Бакет закрыт (см. комментарий в apps/api/src/modules/media/media.controller.ts):
 * постоянных адресов у объектов нет. Единственный адрес картинки — редирект
 * GET /api/media/:id (и /api/media/:id?size=thumb). Он публичный, и это важно:
 * тег <img> заголовок Authorization всё равно не отправит.
 *
 * Редирект ведёт на подписанную ссылку, поэтому ключ объекта и имя бакета
 * клиенту видны — секретом они и не были, доступ даёт только подпись.
 *
 * `:id` — ПУБЛИЧНЫЙ идентификатор, строка из 22 символов, а не номер строки в
 * таблице: по номеру картинки перебирались подряд.
 */

/** База API считается так же, как в api/client.ts: там она приватная. */
const BASE: string = (import.meta.env.VITE_API_BASE ?? '').replace(/\/+$/, '');

/**
 * Картинка так, как её отдаёт API: {id, url, thumbUrl}, где адреса — это
 * `/api/media/${id}`. Ключи S3 сюда не приходят никогда, договорённость на
 * весь проект.
 *
 * Ссылки объявлены необязательными не ради старых ответов, а ради вызовов
 * вида imageSrc({ id: pet.imageId }): половина экранов знает про картинку
 * только её id (appearanceImageId, imageId ревизии) — и этого достаточно,
 * адрес собирается из id.
 */
export interface ApiImage {
  id: string;
  url?: string | null;
  thumbUrl?: string | null;
}

export type ImageSize = 'full' | 'thumb';

export interface UploadedImage {
  id: string;
  url: string;
  thumbUrl: string;
}

/** Предел загрузки на сервере. Проверяем и на клиенте: 15 МБ по мобильной сети — минуты. */
export const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;

/**
 * Абсолютный адрес. Относительный путь нельзя отдавать в VK Bridge: нативная
 * галерея открывает ссылки сама и про наш origin ничего не знает.
 */
export function absoluteUrl(path: string): string {
  const withBase = path.startsWith('/') ? `${BASE}${path}` : path;
  return new URL(withBase, window.location.href).href;
}

/** Адрес картинки нужного размера. undefined = картинки нет. */
export function imageSrc(
  image: ApiImage | null | undefined,
  size: ImageSize = 'thumb',
): string | undefined {
  if (!image?.id) return undefined;

  const direct = size === 'thumb' ? (image.thumbUrl ?? image.url) : image.url;
  if (direct) return absoluteUrl(direct);

  return absoluteUrl(`/api/media/${image.id}${size === 'thumb' ? '?size=thumb' : ''}`);
}

/**
 * Загрузка файла. Мимо api/client.ts: тот всегда шлёт JSON, а здесь нужен
 * multipart. Заголовок Content-Type руками не ставим — границу multipart
 * умеет проставить только браузер.
 */
export async function uploadImage(file: File): Promise<UploadedImage> {
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new ApiError(413, t('lib.upload.tooLarge', { limit: MAX_UPLOAD_BYTES / 1024 / 1024 }));
  }

  const body = new FormData();
  body.append('file', file);

  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(`${BASE}/api/media/upload`, { method: 'POST', headers, body });
  } catch {
    throw new ApiError(0, t('lib.upload.offline'));
  }

  const text = await response.text();
  const parsed: unknown = text ? safeParse(text) : null;

  if (!response.ok) {
    throw new ApiError(response.status, readMessage(parsed) ?? t('lib.upload.failed'));
  }

  return parsed as UploadedImage;
}

/** Текстовые ответы (экспорт анкеты) через api/client.ts не проходят: он ждёт JSON. */
export async function fetchText(path: string): Promise<string> {
  let response: Response;
  try {
    response = await fetch(absoluteUrl(path), { headers: { Accept: 'text/plain' } });
  } catch {
    throw new ApiError(0, t('lib.upload.offline'));
  }

  const text = await response.text();
  if (!response.ok) {
    throw new ApiError(
      response.status,
      readMessage(safeParse(text)) ?? t('lib.upload.requestFailed'),
    );
  }
  return text;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/** Nest кладёт текст в message; ValidationPipe — список строк. */
function readMessage(body: unknown): string | null {
  if (typeof body !== 'object' || body === null) return null;
  const message = (body as { message?: unknown }).message;
  if (typeof message === 'string' && message) return message;
  if (Array.isArray(message) && typeof message[0] === 'string') return message[0];
  return null;
}
